import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { useLicenseStore } from "stores/v2/licenseStore";
import Nav from "../Nav";
import { TabBar } from "../TabBar";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useClassroomDetailsQuery } from "@/query/classroomDetailsQuery";
import _ from "lodash";
import shallow from "zustand/shallow";
import useStore from "stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { useMount } from "react-use";
import { getCoursesMicrofrontendUrl } from "js/utils/getMicrofrontendUrl";
import LoadingFallback from "./LoadingFallback";
import ErrorFallback from "./ErrorFallback";
import { useThemeStore } from "stores/v2/themeStore";
import { CLASSROOM_TYPES } from "types/classrooms";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import ErrorBoundary from "@/components/ErrorBoundary";
import ModuleLoadingFallback from "../ModuleLoadingFallback";
import { loadDynamicRemote } from "@/utils/loadDynamicRemote";

const CoursesWindow = () => {
	const { i18n } = useTranslation();
	const params = useParams();
	const licenseStore = useLicenseStore();
	const themeStore = useThemeStore();

	const classroomStore = useStore(
		(state) => ({
			getClassroomV2: state.classroom.getClassroomV2,
			fetchClassrooms: state.classroom.fetchClassrooms,
		}),
		shallow
	);

	const selectedClassroomStore = useSelectedClassroomStore(
		(state) => ({
			setData: state.setData,
		}),
		shallow
	);

	// purposefully only contains a small amount of the classroom info, use classroomQuery.data for full object. EDU-4354 should remove the requirement for this state.
	const [classroom, setClassroom] = useState(
		_.pick(classroomStore.getClassroomV2(params.classId), ["id", "name", "type", "sourceId", "personalisation", "description"])
	);

	const classroomQuery = useClassroomDetailsQuery(
		params.classId,
		true,
		true,
		false,
		classroom?.type,
		classroom?.sourceId,
		useUserSessionStore.getState().getTeacherId(),
		{
			enabled: classroom && !_.isEmpty(classroom),
			select: (data) => data.classroom,
		}
	);

	// actions to run when classroomQuery data is available/updated
	useEffect(() => {
		if (!classroomQuery.data || !classroom.personalisation) {
			return;
		}
		setClassroom({ ...classroomQuery.data, personalisation: classroom.personalisation });
	}, [classroomQuery.data, classroom.personalisation]);

	const isClassroomDataAvailable = !_.isEmpty(classroomStore.getClassroomV2(params.classId));

	// fetch classrooms if it isn't already fetched - this occurs when the active classroom page is refreshed or visited directly, rather then navigating from classrooms overview
	useMount(async () => {
		if (!isClassroomDataAvailable) {
			await classroomStore.fetchClassrooms();
			const _classroom = classroomStore.getClassroomV2(params.classId);
			setClassroom(_classroom);
			selectedClassroomStore.setData(_classroom);
		}
	});

	const classroomType = classroom.type.toLowerCase() === CLASSROOM_TYPES.CLASSROOM ? "CLASS" : "GROUP";
	// Stable per mount: a new `lazy()` every render (with a changing ?nocache URL) remounted the MFE and could strand `locale` at the first paint value (e.g. `en` before account settings apply `es`).
	const coursesModuleRemoteUrl = useMemo(() => getCoursesMicrofrontendUrl(window.location.hostname) + "?nocache=" + String(Date.now()), []);

	const DynamicRemoteApp = useMemo(
		() => lazy(() => loadDynamicRemote(coursesModuleRemoteUrl, "CoursesModule", "./app", "vite", "esm")),
		[coursesModuleRemoteUrl]
	);

	const coursesLocale = i18n.resolvedLanguage ?? i18n.language;

	const isClassActive = useStore((state) => getClassroomStore(state).isClassSessionActiveV2(params.classId));

	return (
		<Box>
			<Nav selectedClass={classroom} />
			<TabBar isActive={isClassActive} />
			{!!classroomQuery.data && (
				<ErrorBoundary fallback={<ErrorFallback />}>
					<Suspense fallback={<LoadingFallback /> ?? <ModuleLoadingFallback />}>
						{/* Remount MFE when host language changes so Courses i18n re-inits (federated subtree otherwise stays on first language). */}
						<DynamicRemoteApp
							key={coursesLocale}
							classId={params.classId}
							classType={classroomType}
							isLicensed={licenseStore.courses}
							classwizeTheme={themeStore.theme}
							applianceId={localStorage.getItem("DEVICE")}
							token={localStorage.getItem("TOKEN")}
							locale={coursesLocale}
						/>
					</Suspense>
				</ErrorBoundary>
			)}
		</Box>
	);
};

export default CoursesWindow;
